if (!customElements.get('pdp-hero-gallery')) {
  class PdpHeroGallery extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
    }

    connectedCallback() {
      this.slides = this.querySelectorAll('[data-pdp-slide]');
      this.thumbs = this.querySelectorAll('[data-pdp-thumb]');
      this.dots = this.querySelectorAll('[data-pdp-dot]');
      this.mainWrap = this.querySelector('[data-pdp-main-img]');
      this.buybar = this.querySelector('[data-pdp-buybar]');
      this.ctaRow = this.querySelector('[data-pdp-cta-row]');

      this.glyph = this.querySelector('.section-pdp-hero__glyph');

      this.bindGallery();
      this.bindGlyphLoading();
      this.bindWishlist();
      this.bindStickyBar();
      this.bindBuybarLoading();
      this.bindSwipe();
      this.bindVariantMediaChange();
      this.bindVariantResolver();
      this.bindAccordion();
      this.bindSizeValidation();
      this.bindSizeSheet();
    }

    bindVariantResolver() {
      this.productInfo = this.closest('product-info');
      if (!this.productInfo) return;

      const variantsScript = this.productInfo.querySelector('[data-pdp-variants]');
      if (!variantsScript) return;

      try {
        this.pdpVariants = JSON.parse(variantsScript.textContent);
      } catch (error) {
        return;
      }
      if (!Array.isArray(this.pdpVariants) || this.pdpVariants.length === 0) return;

      this.pdpSectionId = this.productInfo.dataset.section;
      this.resolveVariant = this.resolveVariant.bind(this);

      // Delegated so it survives <variant-selects> being replaced on re-render.
      this.productInfo.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects')) this.resolveVariant();
      });

      // Re-assert the correct variant id after the server round-trip, so a
      // fallback/default variant from the section re-render can't overwrite it.
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.pdpSectionId) return;
          this.resolveVariant();
        });
      }

      // Sync once on load in case the checked defaults differ from the initial id.
      this.resolveVariant();
    }

    resolveVariant() {
      const variantSelects = this.productInfo?.querySelector('variant-selects');
      const idInput = document
        .getElementById(`product-form-${this.pdpSectionId}`)
        ?.querySelector('input[name="id"]');
      if (!variantSelects || !idInput) return;

      // Collect the selected option value for each rendered option, keyed by its
      // 1-based option position (from the input name, e.g. "Size-2" -> 2).
      const selected = {};
      variantSelects.querySelectorAll('fieldset input:checked').forEach((input) => {
        const position = parseInt(input.name.split('-').pop(), 10);
        if (!Number.isNaN(position)) selected[position] = input.value;
      });

      const positions = Object.keys(selected);
      if (positions.length === 0) return;

      // Match on the rendered options only; options skipped from the picker
      // (e.g. colour) are constant across this product's variants.
      const match = this.pdpVariants.find((variant) =>
        positions.every((position) => variant.options[position - 1] === selected[position])
      );
      if (!match) return;

      if (idInput.value !== String(match.id)) {
        idInput.value = match.id;
        idInput.disabled = false;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    bindAccordion() {
      this.accItems = this.querySelectorAll('[data-pdp-acc]');
      if (!this.accItems.length) return;

      this.accReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.accItems.forEach((item) => {
        const head = item.querySelector('.section-pdp-hero__acc-head');
        const body = item.querySelector('.section-pdp-hero__acc-body');
        if (!head || !body) return;

        if (item.open) item.classList.add('is-open');

        head.addEventListener('click', (event) => {
          event.preventDefault();
          if (item.dataset.animating) return;

          if (item.open) {
            this.closeAccItem(item, body);
          } else {
            if (this.accItems.length >= 2) {
              this.accItems.forEach((other) => {
                if (other !== item && other.open && !other.dataset.animating) {
                  this.closeAccItem(other, other.querySelector('.section-pdp-hero__acc-body'));
                }
              });
            }
            this.openAccItem(item, body);
          }
        });
      });
    }

    openAccItem(item, body) {
      item.open = true;
      item.classList.add('is-open');
      if (this.accReduceMotion) return;

      item.dataset.animating = '1';
      body.style.overflow = 'hidden';
      body.style.height = '0px';
      const target = body.scrollHeight;

      requestAnimationFrame(() => {
        body.style.height = `${target}px`;
      });

      this.onAccTransitionEnd(item, body);
    }

    closeAccItem(item, body) {
      item.classList.remove('is-open');
      if (this.accReduceMotion) {
        item.open = false;
        return;
      }

      item.dataset.animating = '1';
      body.style.overflow = 'hidden';
      body.style.height = `${body.scrollHeight}px`;

      requestAnimationFrame(() => {
        body.style.height = '0px';
      });

      this.onAccTransitionEnd(item, body, () => {
        item.open = false;
      });
    }

    onAccTransitionEnd(item, body, done) {
      const handler = (event) => {
        if (event.propertyName !== 'height') return;
        body.removeEventListener('transitionend', handler);
        if (done) done();
        body.style.height = '';
        body.style.overflow = '';
        delete item.dataset.animating;
      };
      body.addEventListener('transitionend', handler);
    }

    // Queried live rather than cached: <variant-selects> is replaced with a fresh
    // node on every re-render, which would leave a stored reference detached.
    get sizeGroup() {
      return this.querySelector('.section-pdp-hero__size-group');
    }

    get selectedSizeInput() {
      return this.sizeGroup?.querySelector('input:checked') ?? null;
    }

    /* Whoever asked says why: an add-to-cart wants the add finished once a
       size is chosen, Build a Pack wants its picker opened instead. Without
       this the sheet always submitted the form, so a shopper who pressed
       "Add any 2" got one item in the cart. */
    handleSizeRequest(event) {
      this.sizeIntent = event?.detail?.intent ?? 'add';
      this.requestSize();
    }

    /* Mobile asks for the size in a bottom sheet; desktop keeps the inline
       error beside the options. */
    requestSize() {
      if (this.sizeSheet && this.isMobileViewport()) {
        this.openSizeSheet();
        return;
      }

      const { sizeGroup } = this;
      if (!sizeGroup) return;

      const sizeError = sizeGroup.querySelector('[data-size-error]');
      if (sizeError) sizeError.hidden = false;
      sizeGroup.classList.add('has-error');

      sizeGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    bindSizeValidation() {
      if (!this.sizeGroup) return;

      this.handleSizeRequest = this.handleSizeRequest.bind(this);

      // Capture-phase listener on the wrapper runs before ProductForm's
      // bubble-phase listener on the inner <form>.
      this.addEventListener('submit', (event) => {
        if (this.selectedSizeInput) return;

        event.preventDefault();
        event.stopPropagation();
        this.sizeIntent = 'add';
        this.requestSize();
      }, true);

      // Anything else that needs a size before it can act — the Build a Pack
      // tiers, which never submit this form — asks for it the same way, so
      // there is one prompt on the page rather than one per buy control.
      document.addEventListener('theme:pdp:size-required', this.handleSizeRequest);

      // Delegated so it survives <variant-selects> being replaced on re-render.
      this.addEventListener('change', (event) => {
        const sizeGroup = event.target.closest('.section-pdp-hero__size-group');
        if (!sizeGroup) return;

        const sizeError = sizeGroup.querySelector('[data-size-error]');
        if (sizeError) sizeError.hidden = true;
        sizeGroup.classList.remove('has-error');

        this.syncSheetSelection();
      });

      // Tapping the buy-bar size label scrolls to the size selector.
      const bbSize = this.querySelector('.section-pdp-hero__bb-size');
      if (bbSize) {
        bbSize.addEventListener('click', () => {
          if (this.sizeSheet && this.isMobileViewport() && !this.selectedSizeInput) {
            this.openSizeSheet();
            return;
          }

          this.sizeGroup?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }

    bindSizeSheet() {
      this.sizeSheet = this.querySelector('[data-pdp-size-sheet]');
      if (!this.sizeSheet || !this.sizeGroup) return;

      this.sheetDialog = this.sizeSheet.querySelector('[data-pdp-sheet-dialog]');
      this.sheetSizes = this.sizeSheet.querySelector('[data-pdp-sheet-sizes]');
      this.sheetForm = document.getElementById(`product-form-${this.closest('product-info')?.dataset?.section}`);
      this.sheetMediaQuery = window.matchMedia('(max-width: 749px)');

      this.onSheetClick = this.onSheetClick.bind(this);
      this.onSheetKeydown = this.onSheetKeydown.bind(this);

      // One delegated listener covers every chip plus both close affordances.
      this.sizeSheet.addEventListener('click', this.onSheetClick);
      document.addEventListener('keydown', this.onSheetKeydown);

      // The add either lands in the cart or errors out; both end the sheet's turn.
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.sheetUnsubscribers = [
          subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.closeSizeSheet()),
          subscribe(PUB_SUB_EVENTS.cartError, () => this.closeSizeSheet()),
        ];
      }

      // Chips start in sync with whatever the picker already has selected;
      // later changes come through the delegated listener in bindSizeValidation.
      this.syncSheetSelection();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onSheetKeydown);
      document.removeEventListener('theme:pdp:size-required', this.handleSizeRequest);

      if (this.sheetUnsubscribers) {
        for (const unsubscribe of this.sheetUnsubscribers) unsubscribe();
      }

      document.body.classList.remove('pdp-size-sheet-open');
    }

    isMobileViewport() {
      return this.sheetMediaQuery?.matches ?? window.innerWidth < 750;
    }

    isSizeSheetOpen() {
      return this.sizeSheet?.classList.contains('is-open') ?? false;
    }

    openSizeSheet() {
      if (!this.sizeSheet || this.isSizeSheetOpen()) return;

      this.sheetReturnFocus = document.activeElement;
      this.sizeSheet.classList.add('is-open');
      document.body.classList.add('pdp-size-sheet-open');

      const firstSize = this.sheetSizes?.querySelector('[data-pdp-sheet-size]:not([disabled])');
      if (typeof trapFocus === 'function') trapFocus(this.sheetDialog, firstSize ?? this.sheetDialog);
    }

    closeSizeSheet() {
      // cartUpdate also fires for cart-drawer edits, so ignore it unless this is ours.
      if (!this.isSizeSheetOpen()) return;

      this.sizeSheet.classList.remove('is-open', 'is-busy');
      document.body.classList.remove('pdp-size-sheet-open');
      this.sheetBusy = false;

      for (const chip of this.sheetSizes?.querySelectorAll('.is-loading') ?? []) {
        chip.classList.remove('is-loading');
      }

      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.sheetReturnFocus ?? undefined);
      this.sheetReturnFocus = null;
    }

    onSheetKeydown(event) {
      if (event.key !== 'Escape' || !this.isSizeSheetOpen()) return;

      // A size guide opened from the sheet takes Escape first.
      if (document.body.classList.contains('size-guide-open')) return;

      this.closeSizeSheet();
    }

    onSheetClick(event) {
      if (event.target.closest('[data-pdp-sheet-close]')) {
        this.closeSizeSheet();
        return;
      }

      const chip = event.target.closest('[data-pdp-sheet-size]');
      if (!chip || chip.disabled) return;

      this.selectSheetSize(chip);
    }

    selectSheetSize(chip) {
      if (this.sheetBusy) return;

      // Looked up by id every time: <variant-selects> is replaced on re-render.
      const input = document.getElementById(chip.dataset.inputId);
      if (!input || input.disabled) return;

      input.checked = true;
      // Bubbles to <variant-selects> and to the resolver that writes the variant id.
      input.dispatchEvent(new Event('change', { bubbles: true }));

      this.syncSheetSelection();

      // The pack picker is what wanted the size — it reopens itself off the
      // change above, so this sheet's turn is simply over.
      if (this.sizeIntent === 'pack') {
        this.sizeIntent = null;
        this.closeSizeSheet();
        return;
      }

      this.sheetBusy = true;
      chip.classList.add('is-loading');
      this.sizeSheet.classList.add('is-busy');

      if (!this.sheetForm) {
        this.closeSizeSheet();
        return;
      }

      // ProductForm reads its own submit button, so no submitter is needed here.
      if (typeof this.sheetForm.requestSubmit === 'function') {
        this.sheetForm.requestSubmit();
      } else {
        this.querySelector('.section-pdp-hero__btn-bag')?.click();
      }
    }

    syncSheetSelection() {
      if (!this.sheetSizes) return;

      const checkedId = this.sizeGroup?.querySelector('input:checked')?.id;

      for (const chip of this.sheetSizes.querySelectorAll('[data-pdp-sheet-size]')) {
        const isSelected = Boolean(checkedId) && chip.dataset.inputId === checkedId;
        chip.classList.toggle('is-selected', isSelected);
        chip.setAttribute('aria-pressed', String(isSelected));
      }
    }

    bindGallery() {
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this.goTo(parseInt(thumb.dataset.index, 10));
        });
      });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.goTo(parseInt(dot.dataset.index, 10));
        });
      });
    }

    goTo(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.currentIndex = index;

      this.slides.forEach((slide) => {
        slide.classList.toggle(
          'section-pdp-hero__slide--active',
          parseInt(slide.dataset.index, 10) === index
        );
      });

      this.showGlyphIfLoading(index);

      this.thumbs.forEach((thumb) => {
        thumb.classList.toggle(
          'section-pdp-hero__thumb--active',
          parseInt(thumb.dataset.index, 10) === index
        );
      });
      this.dots.forEach((dot) => {
        dot.classList.toggle(
          'section-pdp-hero__dot--active',
          parseInt(dot.dataset.index, 10) === index
        );
      });

      this.querySelectorAll(`[data-pdp-thumb][data-index="${index}"]`).forEach((thumb) => {
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      });
    }

    goToMediaId(mediaId) {
      const slideIndex = Array.from(this.slides).findIndex(
        (slide) => slide.dataset.mediaId === String(mediaId)
      );
      if (slideIndex !== -1) {
        this.goTo(slideIndex);
      }
    }

    bindGlyphLoading() {
      if (!this.glyph) return;

      const activeSlide = this.querySelector('.section-pdp-hero__slide--active');
      if (!activeSlide) return;

      const img = activeSlide.querySelector('img');
      if (!img) {
        this.glyph.classList.add('is-hidden');
        return;
      }

      if (img.complete && img.naturalWidth > 0) {
        this.glyph.classList.add('is-hidden');
      } else {
        const hide = () => this.glyph.classList.add('is-hidden');
        img.addEventListener('load', hide, { once: true });
        img.addEventListener('error', hide, { once: true });
      }
    }

    showGlyphIfLoading(index) {
      if (!this.glyph) return;

      const slide = this.querySelector(`[data-pdp-slide][data-index="${index}"]`);
      if (!slide) return;

      const img = slide.querySelector('img');
      if (!img || (img.complete && img.naturalWidth > 0)) return;

      this.glyph.classList.remove('is-hidden');

      const hide = () => this.glyph.classList.add('is-hidden');
      img.addEventListener('load', hide, { once: true });
      img.addEventListener('error', hide, { once: true });
    }

    bindWishlist() {
      const favBtn = this.querySelector('[data-pdp-fav]');
      const favOverlay = this.querySelector('[data-pdp-fav-overlay]');
      if (!favBtn && !favOverlay) return;

      const toggle = () => {
        if (favBtn) favBtn.classList.toggle('section-pdp-hero__btn-fav--on');
        if (favOverlay) favOverlay.classList.toggle('section-pdp-hero__fav-overlay--on');
      };

      if (favBtn) favBtn.addEventListener('click', toggle);
      if (favOverlay) favOverlay.addEventListener('click', toggle);
    }

    bindStickyBar() {
      if (!this.buybar || !this.ctaRow) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          this.buybar.classList.toggle('section-pdp-hero__buybar--visible', !entry.isIntersecting);
        },
        { threshold: 0 }
      );
      observer.observe(this.ctaRow);
    }

    bindBuybarLoading() {
      const buybarBtn = this.buybar?.querySelector('.section-pdp-hero__bb-btn');
      const mainBtn = this.querySelector('.section-pdp-hero__btn-bag');
      if (!buybarBtn || !mainBtn) return;

      const buybarSpinner = buybarBtn.querySelector('.loading__spinner');

      const observer = new MutationObserver(() => {
        const isLoading = mainBtn.classList.contains('loading');
        buybarBtn.classList.toggle('loading', isLoading);
        buybarSpinner?.classList.toggle('hidden', !isLoading);
      });

      observer.observe(mainBtn, { attributes: true, attributeFilter: ['class'] });
    }

    bindSwipe() {
      if (!this.mainWrap) return;

      let startX = 0;
      let startY = 0;

      this.mainWrap.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      this.mainWrap.addEventListener('touchend', (e) => {
        const diffX = e.changedTouches[0].clientX - startX;
        const diffY = e.changedTouches[0].clientY - startY;

        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50 && this.slides.length > 1) {
          if (diffX < 0) {
            this.goTo((this.currentIndex + 1) % this.slides.length);
          } else {
            this.goTo((this.currentIndex - 1 + this.slides.length) % this.slides.length);
          }
        }
      }, { passive: true });
    }

    bindVariantMediaChange() {
      if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const sectionId = this.closest('product-info')?.dataset?.section;
        if (data.sectionId !== sectionId) return;

        if (data.variant?.featured_media?.id) {
          this.goToMediaId(data.variant.featured_media.id);
        }
      });
    }
  }

  customElements.define('pdp-hero-gallery', PdpHeroGallery);
}

